import React, { useCallback, useMemo } from 'react';

export type ImportGridRowId = string | number;

export type ImportGridColumn<Row> = {
    key: keyof Row & string;
    header: string;
    required?: boolean;
};

export type ImportGridRowValidation<Row> = {
    cellErrors?: Partial<Record<keyof Row & string, string>>;
    cellWarnings?: Partial<Record<keyof Row & string, string>>;
    rowErrors?: string[];
    rowWarnings?: string[];
};

export type ImportGridSummary = {
    errorCount: number;
    warningCount: number;
    hasBlockingErrors: boolean;
};

export type ImportGridEditorRenderProps<Row> = {
    columns: ImportGridColumn<Row>[];
    rows: Row[];
    updateCell: <ColumnKey extends keyof Row & string>(
        rowId: ImportGridRowId,
        columnKey: ColumnKey,
        value: Row[ColumnKey],
    ) => void;
    removeRow: (rowId: ImportGridRowId) => void;
    appendRow: (row: Row) => void;
    getRowId: (row: Row) => ImportGridRowId;
    getVisibleRowNumber: (rowId: ImportGridRowId) => number | undefined;
    getCellError: (
        rowId: ImportGridRowId,
        columnKey: keyof Row & string,
    ) => string | undefined;
    getCellWarning: (
        rowId: ImportGridRowId,
        columnKey: keyof Row & string,
    ) => string | undefined;
    getRowErrors: (rowId: ImportGridRowId) => string[];
    getRowWarnings: (rowId: ImportGridRowId) => string[];
    summary: ImportGridSummary;
    diagnosticsByRowId: Record<string, Required<ImportGridRowValidation<Row>>>;
};

export interface ImportGridEditorProps<Row> {
    columns: ImportGridColumn<Row>[];
    rows: Row[];
    getRowId: (row: Row) => ImportGridRowId;
    validateRow?: (
        row: Row,
        context: {
            rowIndex: number;
            rows: Row[];
            columns: ImportGridColumn<Row>[];
        },
    ) => ImportGridRowValidation<Row>;
    onRowsChange: (rows: Row[]) => void;
    children: (props: ImportGridEditorRenderProps<Row>) => React.ReactNode;
}

const toKey = (id: ImportGridRowId) => String(id);

const normalizeValidation = <Row,>(
    validation?: ImportGridRowValidation<Row>,
): Required<ImportGridRowValidation<Row>> => ({
    cellErrors: validation?.cellErrors ?? {},
    cellWarnings: validation?.cellWarnings ?? {},
    rowErrors: validation?.rowErrors ?? [],
    rowWarnings: validation?.rowWarnings ?? [],
});

export const ImportGridEditor = <Row,>({
    columns,
    rows,
    getRowId,
    validateRow,
    onRowsChange,
    children,
}: ImportGridEditorProps<Row>) => {
    const rowNumbersById = useMemo(() => {
        const indexLookup = new Map<string, number>();
        rows.forEach((row, index) => {
            indexLookup.set(toKey(getRowId(row)), index + 1);
        });
        return indexLookup;
    }, [rows, getRowId]);

    const diagnosticsByRowId = useMemo(() => {
        const nextDiagnostics: Record<
            string,
            Required<ImportGridRowValidation<Row>>
        > = {};

        rows.forEach((row, rowIndex) => {
            const rowIdKey = toKey(getRowId(row));
            const validation = validateRow
                ? validateRow(row, { rowIndex, rows, columns })
                : undefined;
            nextDiagnostics[rowIdKey] = normalizeValidation(validation);
        });

        return nextDiagnostics;
    }, [rows, columns, getRowId, validateRow]);

    const summary = useMemo<ImportGridSummary>(() => {
        let errorCount = 0;
        let warningCount = 0;

        Object.values(diagnosticsByRowId).forEach((validation) => {
            errorCount += validation.rowErrors.length;
            errorCount += Object.keys(validation.cellErrors).length;
            warningCount += validation.rowWarnings.length;
            warningCount += Object.keys(validation.cellWarnings).length;
        });

        return {
            errorCount,
            warningCount,
            hasBlockingErrors: errorCount > 0,
        };
    }, [diagnosticsByRowId]);

    const updateCell = useCallback<
        ImportGridEditorRenderProps<Row>['updateCell']
    >(
        (rowId, columnKey, value) => {
            const rowIdKey = toKey(rowId);
            const nextRows = rows.map((row) => {
                if (toKey(getRowId(row)) !== rowIdKey) {
                    return row;
                }

                return {
                    ...row,
                    [columnKey]: value,
                };
            });

            onRowsChange(nextRows);
        },
        [rows, getRowId, onRowsChange],
    );

    const removeRow = useCallback(
        (rowId: ImportGridRowId) => {
            const rowIdKey = toKey(rowId);
            onRowsChange(
                rows.filter((row) => toKey(getRowId(row)) !== rowIdKey),
            );
        },
        [rows, getRowId, onRowsChange],
    );

    const appendRow = useCallback(
        (row: Row) => {
            onRowsChange([...rows, row]);
        },
        [rows, onRowsChange],
    );

    const getVisibleRowNumber = useCallback(
        (rowId: ImportGridRowId) => rowNumbersById.get(toKey(rowId)),
        [rowNumbersById],
    );

    const getCellError = useCallback(
        (rowId: ImportGridRowId, columnKey: keyof Row & string) =>
            diagnosticsByRowId[toKey(rowId)]?.cellErrors[columnKey],
        [diagnosticsByRowId],
    );

    const getCellWarning = useCallback(
        (rowId: ImportGridRowId, columnKey: keyof Row & string) =>
            diagnosticsByRowId[toKey(rowId)]?.cellWarnings[columnKey],
        [diagnosticsByRowId],
    );

    const getRowErrors = useCallback(
        (rowId: ImportGridRowId) =>
            diagnosticsByRowId[toKey(rowId)]?.rowErrors ?? [],
        [diagnosticsByRowId],
    );

    const getRowWarnings = useCallback(
        (rowId: ImportGridRowId) =>
            diagnosticsByRowId[toKey(rowId)]?.rowWarnings ?? [],
        [diagnosticsByRowId],
    );

    return (
        <>
            {children({
                columns,
                rows,
                updateCell,
                removeRow,
                appendRow,
                getRowId,
                getVisibleRowNumber,
                getCellError,
                getCellWarning,
                getRowErrors,
                getRowWarnings,
                summary,
                diagnosticsByRowId,
            })}
        </>
    );
};

export default ImportGridEditor;
